import { useState, createContext, useContext } from "react";
import { useTheme } from "@repo/tokens";
import { resolveThemeToken } from "../../utils/theme";
import { Text } from "../../primitives/text";
import { VStack } from "../../primitives/stack";
import { type RadioProps, type RadioGroupProps } from "./types";

// --- Group Context ---
interface RadioGroupContextValue {
  value?: string;
  onChange: (val: string) => void;
  isDisabled?: boolean;
  size?: "sm" | "md" | "lg";
  name?: string;
}
const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

// --- Radio Component ---
export const Radio = ({
  value,
  label,
  size = "md",
  isDisabled,
  isInvalid,
  id,
  testID,
  accessibilityLabel,
}: RadioProps) => {
  const { theme } = useTheme();
  const group = useContext(RadioGroupContext);

  const computedIsDisabled = group?.isDisabled || isDisabled;
  const computedSize = group?.size || size;
  const isChecked = group?.value === value;
  const name = group?.name;

  const handleChange = () => {
    if (computedIsDisabled) return;
    group?.onChange(value);
  };

  // Styling
  const boxSize = computedSize === "sm" ? 16 : computedSize === "lg" ? 24 : 20;
  const borderWidth = 2;
  const gapBetweenBorderDot = 2;
  // dot = box - borders - gaps
  const dotSize = boxSize - borderWidth * 2 - gapBetweenBorderDot * 2;
  const labelTextSize: "small" | "medium" | "large" =
    computedSize === "sm"
      ? "small"
      : computedSize === "lg"
        ? "large"
        : "medium";

  // State Colors
  let borderColor = theme.colors.neutral[50];
  let bgColor = "surface.input";
  let activeColor = theme.colors.primary[40];

  if (computedIsDisabled) {
    borderColor = "control.border.disabled";
    bgColor = "control.background.disabled";
    activeColor = "action.primary.disabled";
  } else if (isInvalid) {
    borderColor = "control.border.error";
  } else if (isChecked) {
    borderColor = theme.colors.primary[40];
  }

  const resolvedBorder = resolveThemeToken(theme, borderColor);
  const resolvedBg = resolveThemeToken(theme, bgColor);
  const resolvedActive = resolveThemeToken(theme, activeColor);

  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        cursor: computedIsDisabled ? "not-allowed" : "pointer",
        opacity: computedIsDisabled ? 0.6 : 1,
        gap: 12,
        width: "100%",
        minWidth: 0,
      }}
      data-testid={testID}
    >
      <div
        style={{
          position: "relative",
          width: boxSize,
          height: boxSize,
          flexShrink: 0,
          boxSizing: "content-box",
        }}
      >
        <input
          type="radio"
          id={id}
          name={name}
          value={value}
          checked={isChecked}
          onChange={handleChange}
          disabled={computedIsDisabled}
          aria-label={accessibilityLabel || label}
          aria-invalid={isInvalid}
          style={{
            position: "absolute",
            opacity: 0,
            width: "100%",
            height: "100%",
            cursor: "inherit",
            margin: 0,
          }}
        />

        {/* Custom Box */}
        <div
          style={{
            width: boxSize,
            height: boxSize,
            borderWidth: borderWidth,
            borderStyle: "solid",
            borderColor: resolvedBorder,
            backgroundColor: resolvedBg,
            borderRadius: "50%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
          }}
        >
          <div
            style={{
              width: dotSize,
              height: dotSize,
              borderRadius: "50%",
              backgroundColor: isChecked
                ? resolvedActive
                : theme.colors.neutral[100],
            }}
          />
        </div>
      </div>
      {label && (
        <Text
          as="span"
          variant="body"
          size={labelTextSize}
          weight="regular"
          color={
            computedIsDisabled ? "text.disabled" : theme.colors.neutral[20]
          }
        >
          {label}
        </Text>
      )}
    </label>
  );
};

// --- Group Component ---
export const RadioGroup = ({
  value,
  defaultValue,
  onChange,
  isDisabled,
  children,
  label,
  name,
  direction = "column",
  gap = "sm",
  style,
}: RadioGroupProps) => {
  const [internalValue, setInternalValue] = useState<string | undefined>(
    defaultValue,
  );

  const currentValue = value !== undefined ? value : internalValue;

  const handleChange = (val: string) => {
    if (value === undefined) {
      setInternalValue(val); // Uncontrolled
    }
    onChange?.(val);
  };

  return (
    <RadioGroupContext.Provider
      value={{ value: currentValue, onChange: handleChange, isDisabled, name }}
    >
      <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
        {label && (
          <legend style={{ marginBottom: 8 }}>
            <Text variant="caption" color="text.secondary">
              {label}
            </Text>
          </legend>
        )}
        <VStack
          gap={gap}
          style={{
            ...(direction === "row"
              ? { flexDirection: "row", flexWrap: "wrap" }
              : {}),
            ...style,
          }}
        >
          {children}
        </VStack>
      </fieldset>
    </RadioGroupContext.Provider>
  );
};
